import { useEffect, useState } from 'react';
import type { AppProps } from 'next/app';
import { appWithTranslation } from 'next-i18next';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import type { AppState } from 'redux-store';
import { useAppSelector, wrapper } from 'redux-store';
import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

import logEvent, { initAmplitude, setUserAmplitude } from 'analytics/logEvent';
import type { PublicRuntimeConfig } from 'app.types';
import type { LogRocketService } from 'domains/common/logRocket';

// Styles.
import 'styles/globals.scss';

function App({
  Component,
  pageProps,
  err,
}: AppProps & { err: Record<string, string> }): JSX.Element {

  const { publicRuntimeConfig } = getConfig();
  const { analytics, apiUrl }: PublicRuntimeConfig = publicRuntimeConfig;

  const profile = useAppSelector(appState => {
    return appState.profile;
  });
  const jwtError = useAppSelector<AppState['jwt']['error']>(
    state => state.jwt.error,
  );
  const jwt = useAppSelector<AppState['jwt']['payload']>(
    state => state.jwt.payload,
  );
  const currentExperiments = useAppSelector(
    state => state.experiments.experiments,
  );
  const { userAgent } = useAppSelector(state => state.ui);

  const [isAnalyticsInitialized, setAnalyticsInitialized] = useState(false);
  const [isGTMInitialized, setGTMInitialized] = useState(false);

  const router = useRouter();
  const { pathname, query } = router;

  useEffect(() => {
    async function fn(): Promise<void> {
      if (!isGTMInitialized) {
        let currentGTM = 'GTM-NSGK8PZ';
        if (/survey/.test(pathname) === true) {
          currentGTM = 'GTM-5346Z7WX';
          // currentGTM = 'GTM-WM5DF4SS';

          const TagManager = (await import('@sooro-io/react-gtm-module')).default;
          TagManager.initialize({
            gtmId: currentGTM,
            source: 'https://server-side-tagging-m42ckoioqq-uc.a.run.app/gtm.js',
          });

          setGTMInitialized(true);
          return;
        }

        if (currentGTM) {
          const TagManager = (await import('react-gtm-module')).default;
          TagManager.initialize({
            gtmId: currentGTM,
          });

          setGTMInitialized(true);
        }
      }
    }

    fn();
  }, [router]);

  useEffect(() => {
    if (!jwtError) {
      return;
    }

    logEvent('Web_Payment_Token_Check_Failed', { ...jwtError });
  }, []);

  useEffect(() => {
    if (profile.isAuthenticated === true && profile.profile.id) {
      const userId = profile.profile.id;
      setUserAmplitude(userId);
    }
  }, [profile.isAuthenticated]);

  useEffect(() => {
    async function fn(): Promise<void> {
      if (!isAnalyticsInitialized) {
        const userId = profile.isAuthenticated ? profile.profile.id : undefined;
        
        const device_id = query.ampDeviceId as string;
        const amplitudeOptionsExtra = {
          jwt_user_id: jwt?.user_id || null,
          device_type: userAgent.device.type || 'desktop',
          device_os: userAgent.os.name,
          device_id: device_id,
          domain: window.location.host,
          screen_resolution_height: window.screen.height,
          screen_resolution_weight: window.screen.width,
        };

        const plugins: amplitude.Types.Plugin<amplitude.Types.BrowserClient, amplitude.Types.BrowserConfig>[] = [];
        if (/survey/.test(pathname) === true) {
          const sessionReplayTracking = sessionReplayPlugin({
            sampleRate: 0.05
          });
          plugins.push(sessionReplayTracking);
        }

        initAmplitude({
          userId,
          currentExperiments,
          extra: amplitudeOptionsExtra,
          plugins,
        });

        if (
          typeof window !== 'undefined' &&
          window.location.host == 'app.welltory.com' &&
          window.location.pathname.includes('web-special-offer')
        ) {
          // const sessionId = amplitude.getSessionId();
          const { logRocketService }: { logRocketService: LogRocketService } =
            await import('domains/common/logRocket');

          logRocketService.init({
            userId: `{userId}`,
            // amplitudeClient,
          });
        }

        setAnalyticsInitialized(true);
      }
    }

    fn();
  }, [
    currentExperiments,
    isAnalyticsInitialized,
    profile.isAuthenticated,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    profile.profile?.id,
    analytics.enable,
  ]);

  return <Component {...pageProps} err={err} />;
}

export default wrapper.withRedux(appWithTranslation(App));
